import { useCallback, useMemo, useState } from "react"
import { Avatar, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { SourceProject } from "../../types"
import { PlusIcon } from "shared/icons"
import { SearchDetailResult } from "interface/search"
import { AddProjectPopOver } from "./popOver"
import { dateFormat, flatJoin, numberFormat } from "shared/util"

type ProjectToolTipProps = {
	project: SourceProject
	num: number
}
const ProjectToolTip = ({
	project,
	num
}: ProjectToolTipProps) => {
	return (
		<Stack direction="row" alignItems="flex-start" spacing={1}>
			<Avatar sx={{
				fontSize: "small",
				width: "1.5rem",
				height: "1.5rem",
				bgcolor: "#f5f5f5",
				color: "#000000",
				border: '1px solid #dadde9',
				borderRadius: 1
			}}>{num}</Avatar>
			<Stack direction="column" spacing={0.5}>
				{project.projectDate && <Typography variant="body2">{dateFormat(project.projectDate, { wareki: false })}</Typography>}
				<Typography variant="body1">{project.projectName}</Typography>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Typography variant="body2" sx={{ flexGrow: 1 }}>{project.productName}</Typography>
					<Typography variant="body2" whiteSpace="nowrap">{numberFormat(project.logList.length)} 件</Typography>
				</Stack>
			</Stack>
		</Stack>
	)
}

type ProjectItemProps = {
	project: SourceProject
	num: number
	onDelete?: () => void 
}
const ProjectItem = ({
	project,
	num,
	onDelete
}: ProjectItemProps) => {
	const title = useMemo(
		() => {
			return flatJoin(
				' ',
				project.projectDate ? `${project.projectDate.getMonth() + 1}/${project.projectDate.getDate()}` : null,
				project.projectName
			)
		},
		[project]
	)
	return (
		<Tooltip title={<ProjectToolTip project={project} num={num}/>}>
			<Chip
				avatar={
					<Avatar sx={{
						bgcolor: "#f5f5f5",
						border: '1px solid #dadde9',
						borderRadius: 1
					}}>{num}</Avatar>
				}
				label={title}
				variant="outlined"
				onDelete={onDelete}
				sx={{
					maxWidth: '25em',
					borderRadius: 1
				}}
			/>
		</Tooltip>
	)
}
type ProjectBarProps = {
	search: SearchDetailResult
	projectList: SourceProject[]
	setProjectList: (projectList: SourceProject[]) => void
}
export const ProjectBar = ({
	search,
	projectList,
	setProjectList
}: ProjectBarProps) => {
	const [popup, setPopup] = useState<{
		el: HTMLButtonElement
		type: 'add'
	} | null>(null)
	const onAddProject = useCallback(
		(project: SourceProject) => {
			setProjectList(projectList.concat([project]).slice())
		},
		[projectList, setProjectList]
	)
	const onDeleteProject = useCallback(
		(index: number) => {
			setProjectList(projectList.filter((_, i) => i !== index).slice())
		},
		[projectList, setProjectList]
	)
  return (
    <Stack direction="row" spacing={1} paddingY={0.5} paddingX={1} alignItems="center">
			<Stack flexGrow={1} direction="row" flexWrap="wrap" alignItems="center" spacing={1} useFlexGap>
				{projectList.map((project, i) => (
					<ProjectItem
						key={`project-${i}`}
						project={project}
						num={i+1}
						onDelete={project.isOrigin === true ? undefined : () => onDeleteProject(i)}
					/>
				))}
			</Stack>

			<Tooltip title="プロジェクト追加" placement="bottom-end">
				<IconButton
					onClick={(ev) => {
						setPopup({
							el: ev.currentTarget,
							type: 'add'
						})
					}}
				>
					<PlusIcon/>
				</IconButton>
			</Tooltip>

			{popup && (
				<AddProjectPopOver
					anchorEl={popup.el}
					search={search}
					defaultProject={projectList.length > 0 ? projectList[0] : null}
					projectList={projectList}
					onClose={() => setPopup(null)}
					onAddProject={(project) => {
						onAddProject(project)
						setPopup(null)
					}}
				/>
			)}
		</Stack>
	)
}